import React, { useState, useEffect, useRef } from 'react';
import { 
    Search, Check, ChevronRight, ChevronLeft, Save, 
    Plus, Trash2, Microscope, Stethoscope, ClipboardList, 
    Activity, FileText, Upload, Sliders, X, AlertCircle, Filter, Pill
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const ClinicalEncounter = ({ appointment, onClose }) => {
    const [step, setStep] = useState(1);
    const [complaints, setComplaints] = useState([]);
    const [selectedComplaints, setSelectedComplaints] = useState([]);
    const [compSearch, setCompSearch] = useState('');
    const [showCompModal, setShowCompModal] = useState(false);

    const [labTests, setLabTests] = useState([]);
    const [selectedLabs, setSelectedLabs] = useState([]);
    const [labMode, setLabMode] = useState(null); // 'laboratory' or 'rapid'
    const [showLabModal, setShowLabModal] = useState(false);
    const [labSearch, setLabSearch] = useState('');

    const [diagnosisList, setDiagnosisList] = useState([]);
    const [selectedDiagnosis, setSelectedDiagnosis] = useState([]);
    const [showDiagModal, setShowDiagModal] = useState(false);

    const [medicineList, setMedicineList] = useState([]);
    const [prescription, setPrescription] = useState([]);
    const [showMedModal, setShowMedModal] = useState(false);
    const [activePrescriptionIdx, setActivePrescriptionIdx] = useState(null);

    const [formData, setFormData] = useState({
        symptomsNotes: '',
        examinationNotes: '',
        investigationNotes: '',
        diagnosisNotes: '',
        plan: ''
    });

    useEffect(() => {
        fetchMetadata();
        if (appointment?.id) fetchExistingNote();
    }, [appointment]);

    useEffect(() => {
        if (appointment?.id && step > 1) handleSave();
    }, [step]);

    const fetchMetadata = async () => {
        try {
            const [comp, lab, diag, med] = await Promise.all([
                axios.get(`${window.location.protocol}//${window.location.hostname}/api/clinical/complaints`),
                axios.get(`${window.location.protocol}//${window.location.hostname}/api/clinical/lab-tests`),
                axios.get(`${window.location.protocol}//${window.location.hostname}/api/clinical/diagnosis`),
                axios.get(`${window.location.protocol}//${window.location.hostname}/api/clinical/medicines`)
            ]);
            setComplaints(comp.data);
            setLabTests(lab.data);
            setDiagnosisList(diag.data);
            setMedicineList(med.data);
        } catch (err) { console.error(err); }
    };

    const fetchExistingNote = async () => {
        try {
            const res = await axios.get(`http://localhost:5000/api/clinical/encounters/${appointment.id}`);
            if (res.data.id) {
                setFormData({
                    symptomsNotes: res.data.symptomsNotes || '',
                    examinationNotes: res.data.examinationNotes || '',
                    investigationNotes: res.data.investigationNotes || '',
                    diagnosisNotes: res.data.diagnosis || '',
                    plan: res.data.plan || ''
                });
                setSelectedComplaints(res.data.complaints || []);
                setSelectedLabs(res.data.investigationRequests || []);
                setPrescription(res.data.prescription || []);
                setSelectedDiagnosis(res.data.selectedDiagnosis || []);
            }
        } catch (err) { console.error(err); }
    };

    const handleSave = async () => {
        try {
            const payload = {
                ...formData,
                appointmentId: appointment.id,
                patientId: appointment.patientId || appointment.PatientId || appointment.Patient?.id,
                doctorId: appointment.doctorId || appointment.DoctorId || appointment.Doctor?.id,
                complaints: selectedComplaints,
                investigationRequests: selectedLabs,
                prescription: prescription,
                selectedDiagnosis: selectedDiagnosis,
                diagnosis: formData.diagnosisNotes
            };
            await axios.post(`${window.location.protocol}//${window.location.hostname}/api/clinical/encounters`, payload);
            toast.success('Record Saved Successfully');
        } catch (err) { toast.error('Save failed'); }
    };

    const steps = [
        { id: 1, name: 'Complaints', icon: Stethoscope },
        { id: 2, name: 'Examination', icon: Activity },
        { id: 3, name: 'Investigations', icon: Microscope },
        { id: 4, name: 'Diagnosis', icon: ClipboardList },
        { id: 5, name: 'Prescription', icon: FileText }
    ];

    const toggleTiming = (idx, t) => {
        const newP = [...prescription];
        const timings = newP[idx].timing || [];
        if (timings.includes(t)) {
            newP[idx].timing = timings.filter(x => x !== t);
        } else {
            newP[idx].timing = [...timings, t];
        }
        setPrescription(newP);
    };

    const SelectionModal = ({ title, items, onSelect, onClose, searchKey = 'name' }) => {
        const [query, setQuery] = useState('');
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300">
                <div className="bg-white w-full max-w-md rounded-[32px] shadow-2xl p-6 animate-in zoom-in-95 duration-200">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-black text-slate-800">{title}</h3>
                        <X className="w-5 h-5 text-slate-400 cursor-pointer" onClick={onClose} />
                    </div>
                    <div className="relative mb-4">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                        <input 
                            autoFocus
                            type="text" 
                            placeholder="Search..."
                            className="w-full bg-slate-50 border-none rounded-2xl py-3 pl-12 pr-4 text-xs font-bold ring-1 ring-slate-100 outline-none focus:ring-2 focus:ring-indigo-500"
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </div>
                    <div className="max-h-80 overflow-y-auto pr-2 space-y-1">
                        {items.filter(i => (i[searchKey] || '').toLowerCase().includes(query.toLowerCase())).map((item, idx) => (
                            <div 
                                key={idx} 
                                onClick={() => { onSelect(item); onClose(); }} 
                                className="p-3.5 hover:bg-indigo-50 rounded-2xl cursor-pointer transition-all flex justify-between items-center group"
                            >
                                <span className="text-[11px] font-bold text-slate-600 group-hover:text-indigo-600">{item[searchKey]}</span>
                                <Plus className="w-4 h-4 text-slate-200 group-hover:text-indigo-400" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="grid grid-cols-12 gap-6 animate-in slide-in-from-right">
                        <div className="col-span-12 bg-white rounded-[32px] p-8 border border-slate-100 shadow-sm">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-black text-slate-800 tracking-tight">Complaint & Symptoms</h3>
                                <button onClick={() => setShowCompModal(true)} className="px-6 py-2.5 bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-100">Select Complaints</button>
                            </div>
                            <div className="flex flex-wrap gap-2 mb-6">
                                {selectedComplaints.map((c, i) => (
                                    <div key={i} className="px-4 py-2 bg-slate-50 border border-slate-200 text-slate-600 rounded-xl text-[11px] font-bold flex items-center gap-2">
                                        {c}
                                        <X className="w-3.5 h-3.5 text-red-400 cursor-pointer" onClick={() => setSelectedComplaints(prev => prev.filter(x => x !== c))} />
                                    </div>
                                ))}
                            </div>
                            <textarea 
                                value={formData.symptomsNotes}
                                onChange={(e) => setFormData({...formData, symptomsNotes: e.target.value})}
                                placeholder="Add additional comments here..."
                                className="w-full h-56 bg-slate-50 border-none rounded-[32px] p-6 text-sm ring-1 ring-slate-100 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                            />
                        </div>
                        {showCompModal && (
                            <SelectionModal 
                                title="Select Complaint"
                                items={complaints}
                                onSelect={(c) => !selectedComplaints.includes(c.name) && setSelectedComplaints([...selectedComplaints, c.name])}
                                onClose={() => setShowCompModal(false)}
                                searchKey="name"
                            />
                        )}
                    </div>
                );
            case 2:
                return (
                    <div className="space-y-6 animate-in slide-in-from-right">
                        <div className="bg-white rounded-[32px] p-8 border border-slate-100 shadow-sm">
                            <h3 className="text-xl font-black text-slate-800 mb-8 border-b border-slate-50 pb-4">Patient Examinations</h3>
                            <div className="bg-[#0f172a] rounded-[40px] aspect-video mb-8 relative border-8 border-[#1e293b] flex items-center justify-center">
                                <div className="absolute top-6 left-1/2 -translate-x-1/2 flex gap-4 bg-white/5 backdrop-blur-2xl p-2.5 rounded-2xl border border-white/10">
                                    <Sliders className="w-5 h-5 text-indigo-400" /><Activity className="w-5 h-5 text-emerald-400" /><Filter className="w-5 h-5 text-amber-400" />
                                </div>
                                <div className="text-center group cursor-pointer">
                                    <div className="w-16 h-16 bg-white/5 rounded-3xl mx-auto mb-4 flex items-center justify-center group-hover:bg-indigo-500 transition-all">
                                        <Upload className="w-6 h-6 text-white/40" />
                                    </div>
                                    <p className="text-xs font-black text-white/30 uppercase tracking-widest">Drop DICOM files here</p>
                                </div>
                            </div>
                            <textarea 
                                value={formData.examinationNotes}
                                onChange={(e) => setFormData({...formData, examinationNotes: e.target.value})}
                                placeholder="Physical examination results..."
                                className="w-full h-32 bg-slate-50 border-none rounded-[32px] p-6 text-sm ring-1 ring-slate-100 outline-none"
                            />
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="space-y-6 animate-in slide-in-from-right">
                        <div className="bg-white rounded-[32px] p-8 border border-slate-100 shadow-sm">
                            <h3 className="text-xl font-black text-slate-800 mb-8">Investigations</h3>
                            <div className="grid grid-cols-2 gap-4 mb-8">
                                <button onClick={() => { setLabMode('laboratory'); setShowLabModal(true); }} className="py-4 bg-[#4f46e5] text-white rounded-3xl text-xs font-black uppercase tracking-widest shadow-xl shadow-indigo-100">Add Lab Tests</button>
                                <button onClick={() => { setLabMode('rapid'); setShowLabModal(true); }} className="py-4 bg-[#45b39d] text-white rounded-3xl text-xs font-black uppercase tracking-widest shadow-xl shadow-emerald-100">Add Rapid Tests</button>
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Selected Lab Tests</h4>
                                    {selectedLabs.filter(l => (l.category || '').toLowerCase() === 'laboratory').map((l, i) => (
                                        <div key={i} className="bg-slate-50 rounded-2xl p-4 flex justify-between items-center border border-slate-100 group">
                                            <div><p className="text-xs font-bold text-slate-800">{l.testName}</p><p className="text-[9px] font-black text-indigo-400 mt-1">${l.price}</p></div>
                                            <button onClick={() => setSelectedLabs(prev => prev.filter(x => x.id !== l.id))} className="text-[9px] font-black text-red-400 uppercase group-hover:bg-red-50 px-3 py-1.5 rounded-lg transition-all">Remove</button>
                                        </div>
                                    ))}
                                </div>
                                <div className="space-y-4">
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Selected Rapid Tests</h4>
                                    {selectedLabs.filter(l => (l.category || '').toLowerCase() === 'rapid').map((l, i) => (
                                        <div key={i} className="bg-slate-50 rounded-2xl p-4 space-y-3 border border-slate-100">
                                            <div className="flex justify-between items-center">
                                                <p className="text-xs font-bold text-slate-800">{l.testName}</p>
                                                <button onClick={() => setSelectedLabs(prev => prev.filter(x => x.id !== l.id))} className="text-[9px] font-black text-red-400 uppercase">Remove</button>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <button className="py-2 bg-indigo-600 text-white rounded-xl text-[9px] font-bold">Upload Result</button>
                                                <select className="bg-white border-none rounded-xl py-1 px-3 text-[9px] font-black uppercase ring-1 ring-slate-100">
                                                    <option>Pending</option><option>Negative</option><option>Positive</option>
                                                </select>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {showLabModal && (
                            <SelectionModal 
                                title={`Select ${labMode === 'laboratory' ? 'Lab' : 'Rapid'} Test`}
                                items={labTests.filter(t => (t.category || '').toLowerCase() === labMode.toLowerCase() && !selectedLabs.find(x => x.id === t.id))}
                                onSelect={(t) => !selectedLabs.find(x => x.id === t.id) && setSelectedLabs([...selectedLabs, t])}
                                onClose={() => setShowLabModal(false)}
                                searchKey="testName"
                            />
                        )}
                    </div>
                );
            case 4:
                return (
                    <div className="space-y-6 animate-in slide-in-from-right">
                        <div className="bg-white rounded-[32px] p-8 border border-slate-100 shadow-sm relative">
                            <div className="flex justify-between items-center mb-8">
                                <h3 className="text-xl font-black text-slate-800 tracking-tight">Diagnosis</h3>
                                <button onClick={() => setShowDiagModal(true)} className="px-6 py-2.5 bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-100">Add Diagnosis</button>
                            </div>
                            <div className="flex flex-wrap gap-2 mb-6">
                                {selectedDiagnosis.map((d, i) => (
                                    <div key={i} className="px-4 py-2 bg-slate-50 border border-slate-200 text-slate-700 rounded-xl text-[11px] font-bold flex items-center gap-2">
                                        <span className="text-indigo-500">{d.code}</span> | {d.name}
                                        <X className="w-3.5 h-3.5 text-red-300 cursor-pointer" onClick={() => setSelectedDiagnosis(prev => prev.filter(x => x.id !== d.id))} />
                                    </div>
                                ))}
                            </div>
                            <textarea 
                                value={formData.diagnosisNotes}
                                onChange={(e) => setFormData({...formData, diagnosisNotes: e.target.value})}
                                placeholder="Diagnosis summary..."
                                className="w-full h-48 bg-slate-50 border-none rounded-[32px] p-6 text-sm ring-1 ring-slate-100 focus:ring-2 focus:ring-indigo-500 outline-none"
                            />
                        </div>
                        {showDiagModal && (
                            <SelectionModal 
                                title="Select Diagnosis (ICD-10)"
                                items={diagnosisList.filter(d => !selectedDiagnosis.find(x => x.id === d.id))}
                                onSelect={(d) => !selectedDiagnosis.find(x => x.id === d.id) && setSelectedDiagnosis([...selectedDiagnosis, d])}
                                onClose={() => setShowDiagModal(false)}
                                searchKey="name"
                            />
                        )}
                    </div>
                );
            case 5:
                return (
                    <div className="space-y-10 animate-in slide-in-from-right pb-20">
                        {selectedDiagnosis.length === 0 ? (
                            <div className="bg-white rounded-[48px] p-20 text-center border border-dashed border-slate-200">
                                <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <ClipboardList className="w-10 h-10 text-slate-300" />
                                </div>
                                <h3 className="text-xl font-black text-slate-400 uppercase tracking-widest">No Diagnosis Selected</h3>
                                <p className="text-xs font-bold text-slate-300 mt-2">Please go back to Step 4 and add a diagnosis before prescribing medicine.</p>
                                <button onClick={() => setStep(4)} className="mt-8 px-10 py-4 bg-indigo-600 text-white rounded-3xl text-[10px] font-black uppercase tracking-widest shadow-2xl shadow-indigo-100">Go to Diagnosis</button>
                            </div>
                        ) : (
                            selectedDiagnosis.map((diag, dIdx) => (
                                <div key={diag.id || dIdx} className="bg-white rounded-[56px] p-10 border border-slate-100 shadow-2xl shadow-slate-200/40 relative overflow-hidden group">
                                    <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
                                    <div className="flex justify-between items-start mb-10">
                                        <div>
                                            <div className="flex items-center gap-3 mb-2">
                                                <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[9px] font-black uppercase tracking-widest border border-indigo-100">{diag.code}</span>
                                                <h3 className="text-2xl font-black text-slate-800 tracking-tight">{diag.name}</h3>
                                            </div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] italic ml-1">Treatment specialized for this condition</p>
                                        </div>
                                        <button 
                                            onClick={() => {
                                                const newP = [...prescription, { diagnosisId: diag.id || diag.name, name: '', dosage: '', duration: '3 Days', when: 'After Food', timing: [] }];
                                                setPrescription(newP);
                                                setActivePrescriptionIdx(newP.length - 1);
                                                setShowMedModal(true);
                                            }} 
                                            className="px-8 py-4 bg-emerald-600 text-white rounded-[24px] text-[10px] font-black uppercase tracking-widest shadow-2xl shadow-emerald-200 hover:bg-slate-900 transition-all active:scale-95 flex items-center gap-3"
                                        >
                                            <Plus className="w-5 h-5" /> Add Medicine
                                        </button>
                                    </div>

                                    <div className="space-y-6">
                                        {prescription.filter(m => m.diagnosisId === (diag.id || diag.name)).map((m, mIdx) => {
                                            const globalIdx = prescription.findIndex(p => p === m);
                                            return (
                                                <div key={mIdx} className="bg-slate-50/50 rounded-[40px] p-8 border border-slate-100 ring-1 ring-slate-100 space-y-8 relative group/card hover:bg-white transition-colors duration-500">
                                                    <div className="flex justify-between items-center">
                                                        <div className="flex items-center gap-4">
                                                            <div className="w-10 h-10 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                                                                <Pill className="w-5 h-5 text-emerald-500" />
                                                            </div>
                                                            <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">{m.name || 'Select Drug...'} <span className="text-indigo-500 ml-2">{m.dosage}</span></h4>
                                                        </div>
                                                        <button onClick={() => setPrescription(prescription.filter((_, i) => i !== globalIdx))} className="p-3 bg-white text-red-400 rounded-2xl hover:bg-red-50 transition-all opacity-0 group-hover/card:opacity-100 shadow-sm border border-slate-50"><Trash2 className="w-4 h-4" /></button>
                                                    </div>

                                                    <div className="grid grid-cols-12 gap-8">
                                                        <div className="col-span-4 space-y-3">
                                                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Frequency</label>
                                                            <div className="flex gap-2">
                                                                {['Morning', 'Noon', 'Evening', 'Night'].map(t => (
                                                                    <button 
                                                                        key={t} 
                                                                        onClick={() => { toggleTiming(globalIdx, t); setTimeout(() => handleSave(), 100); }} 
                                                                        className={`flex-1 py-4 rounded-2xl text-[9px] font-black transition-all ${m.timing?.includes(t) ? 'bg-slate-800 text-white shadow-xl scale-105' : 'bg-white text-slate-300 ring-1 ring-slate-100 hover:ring-indigo-200'}`}
                                                                    >
                                                                        {t[0]}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div className="col-span-4 space-y-3">
                                                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Duration</label>
                                                            <select value={m.duration} onChange={(e) => { const newP = [...prescription]; newP[globalIdx].duration = e.target.value; setPrescription(newP); handleSave(); }} className="w-full p-4 bg-white rounded-2xl text-xs font-black ring-1 ring-slate-100 outline-none appearance-none border-none">
                                                                <option>3 Days</option><option>1 Week</option><option>2 Weeks</option><option>1 Month</option>
                                                            </select>
                                                        </div>
                                                        <div className="col-span-4 space-y-3">
                                                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Instruction</label>
                                                            <div className="flex gap-2">
                                                                {['After Food', 'Before Food'].map(f => (
                                                                    <button key={f} onClick={() => { const newP = [...prescription]; newP[globalIdx].when = f; setPrescription(newP); handleSave(); }} className={`flex-1 py-4 rounded-2xl text-[9px] font-black uppercase transition-all ${m.when === f ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-100' : 'bg-white text-slate-400 ring-1 ring-slate-100'}`}>{f}</button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {prescription.filter(m => m.diagnosisId === (diag.id || diag.name)).length === 0 && (
                                            <div className="py-12 bg-slate-50/50 rounded-[40px] border border-dashed border-slate-200 text-center">
                                                <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest italic">No medication linked to this diagnosis yet.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                        {showMedModal && (
                            <SelectionModal 
                                title="Select Medication"
                                items={medicineList}
                                onSelect={(med) => {
                                    const newP = [...prescription];
                                    newP[activePrescriptionIdx] = { ...newP[activePrescriptionIdx], name: `${med.name} (${med.strength})`, dosage: med.strength };
                                    setPrescription(newP);
                                    setTimeout(() => handleSave(), 100); // Trigger persistent save
                                }}
                                onClose={() => setShowMedModal(false)}
                                searchKey="name"
                            />
                        )}
                    </div>
                );
            default: return null;
        }
    };

    return (
        <div className="bg-[#f8fafc] w-full max-w-6xl mx-auto p-4 md:p-10 rounded-[56px] shadow-2xl overflow-hidden flex flex-col border border-white h-full max-h-[95vh] animate-in fade-in zoom-in duration-500">
            <h2 className="text-3xl font-black text-slate-900 text-center mb-10 tracking-tighter uppercase italic">Clinical Documentation Console</h2>
            
            <div className="flex justify-between items-center mb-12 relative px-8">
                <div className="absolute top-1/2 left-0 w-full h-[2px] bg-slate-100 -translate-y-1/2 z-0" />
                {steps.map((s, idx) => {
                    const Icon = s.icon;
                    const isActive = step === s.id;
                    const isCompleted = step > s.id;
                    return (
                        <div key={s.id} className="relative z-10 flex flex-col items-center group cursor-pointer" onClick={() => setStep(s.id)}>
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-700 border-4 ${
                                isActive ? 'bg-indigo-600 border-white scale-125 shadow-2xl rotate-3' : 
                                isCompleted ? 'bg-emerald-500 border-white rotate-0' : 'bg-white border-slate-50 text-slate-100'
                            }`}>
                                <Icon className={`w-5 h-5 ${isActive || isCompleted ? 'text-white' : 'text-slate-300'}`} />
                            </div>
                            <p className={`mt-4 text-[9px] font-black uppercase tracking-[0.2em] ${isActive ? 'text-indigo-600' : 'text-slate-400'}`}>{s.name}</p>
                        </div>
                    );
                })}
            </div>

            <div className="flex-1 overflow-y-auto px-4 custom-scrollbar-thin">
                {renderStep()}
            </div>

            <div className="mt-8 pt-8 border-t border-slate-100 flex justify-between items-center">
                <button disabled={step === 1} onClick={() => setStep(prev => prev - 1)} className="px-10 py-4 text-slate-400 text-[10px] font-black uppercase tracking-widest hover:text-indigo-600 transition-all disabled:opacity-0">Back</button>
                <div className="flex gap-4">
                    <button onClick={handleSave} className="px-10 py-4 bg-white border border-slate-100 shadow-sm rounded-full text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 transition-all active:scale-95 flex items-center gap-3">
                        <Save className="w-4 h-4 text-slate-400" /> Save Draft
                    </button>
                    {step < 5 ? (
                        <button onClick={() => setStep(prev => prev + 1)} className="px-14 py-4 bg-indigo-600 text-white rounded-full text-[10px] font-black uppercase tracking-widest shadow-2xl shadow-indigo-200 hover:bg-indigo-700 transition-all active:rotate-1">Next Step</button>
                    ) : (
                        <button onClick={() => { handleSave(); onClose(); }} className="px-14 py-4 bg-[#05a660] text-white rounded-full text-[10px] font-black uppercase tracking-widest shadow-2xl shadow-emerald-100 hover:bg-emerald-700 transition-all active:scale-95">Complete Session</button>
                    )}
                </div>
            </div>

            <style dangerouslySetInnerHTML={{ __html: `
                .custom-scrollbar-thin::-webkit-scrollbar { width: 3px; }
                .custom-scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar-thin::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 5px; }
            `}} />
        </div>
    );
};

export default ClinicalEncounter;
